import React, { useCallback, useEffect, useRef, useState } from 'react';

const CAPTCHA_LENGTH = 5;
const CHAR_POOL = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';

function generateCaptchaText(): string {
  let text = '';
  for (let i = 0; i < CAPTCHA_LENGTH; i++) {
    text += CHAR_POOL[Math.floor(Math.random() * CHAR_POOL.length)];
  }
  return text;
}

interface SimpleCaptchaProps {
  onVerified: (verified: boolean) => void;
}

const CANVAS_WIDTH = 180;
const CANVAS_HEIGHT = 44;

export const SimpleCaptcha: React.FC<SimpleCaptchaProps> = ({ onVerified }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [captchaText, setCaptchaText] = useState<string>(generateCaptchaText);
  const [inputValue, setInputValue] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const drawCaptcha = useCallback((text: string) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Background
    ctx.fillStyle = '#f0f4f8';
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Noise lines
    for (let i = 0; i < 5; i++) {
      ctx.beginPath();
      ctx.moveTo(Math.random() * CANVAS_WIDTH, Math.random() * CANVAS_HEIGHT);
      ctx.lineTo(Math.random() * CANVAS_WIDTH, Math.random() * CANVAS_HEIGHT);
      ctx.strokeStyle = `rgba(${Math.random() * 200}, ${Math.random() * 200}, ${Math.random() * 200}, 0.4)`;
      ctx.lineWidth = 1;
      ctx.stroke();
    }

    // Noise dots
    for (let i = 0; i < 40; i++) {
      ctx.fillStyle = `rgba(${Math.random() * 255}, ${Math.random() * 255}, ${Math.random() * 255}, 0.5)`;
      ctx.fillRect(Math.random() * CANVAS_WIDTH, Math.random() * CANVAS_HEIGHT, 2, 2);
    }

    // Draw each character with individual rotation & color
    const colors = ['#e53935', '#1e88e5', '#43a047', '#fb8c00', '#8e24aa', '#00897b'];
    const charWidth = (CANVAS_WIDTH - 30) / text.length;
    const startX = 18;

    for (let i = 0; i < text.length; i++) {
      ctx.save();
      const x = startX + i * charWidth;
      const y = CANVAS_HEIGHT / 2 + (Math.random() - 0.5) * 8;
      ctx.translate(x, y);
      ctx.rotate((Math.random() - 0.5) * 0.4);
      ctx.font = `bold ${18 + Math.floor(Math.random() * 6)}px Arial, sans-serif`;
      ctx.fillStyle = colors[i % colors.length];
      ctx.textBaseline = 'middle';
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }
  }, []);

  useEffect(() => {
    drawCaptcha(captchaText);
  }, [captchaText, drawCaptcha]);

  const refresh = () => {
    setCaptchaText(generateCaptchaText());
    setInputValue('');
    setStatus('idle');
    onVerified(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    if (val === '') {
      setStatus('idle');
      onVerified(false);
      return;
    }

    if (val.length === captchaText.length) {
      if (val === captchaText) {
        setStatus('success');
        onVerified(true);
      } else {
        setStatus('error');
        onVerified(false);
      }
    } else {
      setStatus('idle');
      onVerified(false);
    }
  };

  return (
    <div className="o-feedback__captcha">
      <div className="o-feedback__captcha-row">
        <canvas
          ref={canvasRef}
          width={CANVAS_WIDTH}
          height={CANVAS_HEIGHT}
          className="o-feedback__captcha-canvas"
        />
        <button type="button" className="o-feedback__captcha-refresh" onClick={refresh} title="Refresh">
          ↻
        </button>
      </div>
      <input
        type="text"
        className={`o-feedback__captcha-input ${status !== 'idle' ? `o-feedback__captcha-input--${status}` : ''}`}
        value={inputValue}
        onChange={handleChange}
        placeholder="Nhập mã captcha..."
        maxLength={CAPTCHA_LENGTH}
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
};
